import { z } from 'zod';
import { Email, Id, Timestamp, Uri, atLeastOneField, cursorPage } from './common.js';
import { UploadFileDescriptor, UploadSession } from './uploads.js';

export const GUEST_FORM_DEFAULT_MAX_FILE_MB = 5120;
export const GUEST_FORM_MAX_FILE_MB = 51200;
export const GUEST_FORM_DEFAULT_MIME_PREFIXES = ['image/', 'video/'] as const;

export const GuestUploadForm = z.object({
  id: Id,
  workspace_id: Id,
  board_id: Id,
  board_name: z.string().optional(),
  token: z.string().optional(),
  url: Uri,
  title: z.string().max(200),
  instructions: z.string().max(4000).nullable().optional(),
  require_name: z.boolean(),
  require_email: z.boolean(),
  max_file_mb: z.int().min(1).max(GUEST_FORM_MAX_FILE_MB),
  allowed_mime_prefixes: z.array(z.string()),
  expires_at: Timestamp.nullable().optional(),
  active: z.boolean(),
  uploads_count: z.int(),
  created_by: Id.optional(),
  created_at: Timestamp,
});
export type GuestUploadForm = z.infer<typeof GuestUploadForm>;

export const GuestUploadFormPage = cursorPage(GuestUploadForm);
export type GuestUploadFormPage = z.infer<typeof GuestUploadFormPage>;

export const GuestUploadFormCreate = z.object({
  board_id: Id,
  title: z.string().min(1).max(200),
  instructions: z.string().max(4000).optional(),
  require_name: z.boolean().default(true),
  require_email: z.boolean().default(false),
  max_file_mb: z.int().min(1).max(GUEST_FORM_MAX_FILE_MB).default(GUEST_FORM_DEFAULT_MAX_FILE_MB),
  allowed_mime_prefixes: z.array(z.string()).default([...GUEST_FORM_DEFAULT_MIME_PREFIXES]),
  expires_at: Timestamp.nullable().optional(),
});
export type GuestUploadFormCreate = z.infer<typeof GuestUploadFormCreate>;

export const GuestUploadFormUpdate = atLeastOneField(
  z.object({
    title: z.string().min(1).max(200).optional(),
    instructions: z.string().max(4000).nullable().optional(),
    require_name: z.boolean().optional(),
    require_email: z.boolean().optional(),
    max_file_mb: z.int().min(1).max(GUEST_FORM_MAX_FILE_MB).optional(),
    allowed_mime_prefixes: z.array(z.string()).optional(),
    expires_at: Timestamp.nullable().optional(),
    active: z.boolean().optional(),
  }),
);
export type GuestUploadFormUpdate = z.infer<typeof GuestUploadFormUpdate>;

export const PublicGuestUploadForm = z.object({
  title: z.string(),
  instructions: z.string().nullable().optional(),
  workspace_name: z.string(),
  workspace_logo_url: Uri.nullable().optional(),
  board_name: z.string(),
  require_name: z.boolean(),
  require_email: z.boolean(),
  max_file_mb: z.int(),
  allowed_mime_prefixes: z.array(z.string()),
  expires_at: Timestamp.nullable().optional(),
});
export type PublicGuestUploadForm = z.infer<typeof PublicGuestUploadForm>;

export const GuestUploadCreate = UploadFileDescriptor.extend({
  guest_name: z.string().max(120).optional(),
  guest_email: Email.optional(),
  relative_path: z.string().max(2048).optional(),
});
export type GuestUploadCreate = z.infer<typeof GuestUploadCreate>;

export const GuestUploadSession = UploadSession.extend({
  guest_session_token: z.string(),
  guest_session_expires_at: Timestamp,
});
export type GuestUploadSession = z.infer<typeof GuestUploadSession>;
